/**
 * One playable run: creates the sim, bots, renderer, input and HUD, and drives them with a
 * fixed-step loop (sim at 20 Hz, rendering every animation frame with interpolation).
 *
 * Input latency (see tickDriver.ts / prediction.ts / localMotion.ts):
 *  - a key press runs the next sim tick right away (input-triggered early tick) instead of
 *    waiting up to 50 ms for it; the command → tick mapping is logged (`commandLog`) for replays;
 *  - the button, GCD sweep, cast bar and cast pose show on the press (predicted with the sim's
 *    own canCast + castTiming) and are reconciled when the tick that applied it runs;
 *  - the local player's unit is drawn at the present (extrapolated from held keys), not one
 *    interpolation step in the past; other units keep interpolation.
 */
import type { BotBrain, Command, ContentRegistry, DeepReadonly, RunResult, Sim, SimConfig, SimEvent, UnitId, World } from '@keystone/sim';
import { CHAT_MAX_LEN, TICK_MS, affixesForLevel, castTiming, createSim, jumpHeightAt } from '@keystone/sim';
import { GlideHold, airView } from '../input/glideHold'; // +glide
import { createPartyBots, defaultCompanions } from '@keystone/bots';
import type { Persona, PersonaMode } from '@keystone/content';
import type { KillRewardSummary } from '@keystone/progression';
import { type InRunStart, InRunTally } from '../meta/inrunTally';
import { Hud } from '../hud/hud';
import type { StartConfig } from '../hud/screens';
import { type UiBind, UI_BIND_KEYS, nextTabTarget } from '../input/bindings';
import { type TargetingView, targetCandidates, walkableLos } from '../input/targeting';
import { flashActionBind } from '../hud/actionBar';
import { flashDodge } from '../hud/cluster/dodgeSlot';
import { attachDomInput } from '../input/dom';
import { Steering, forwardAxis, localControl, steersFacing, turnAxis } from '../input/controls'; // +controls
import { controlSettings } from '../input/controlSettings'; // +controls
import { CameraRig, DEFAULT_DISTANCE } from '../render/camera';
import { bindCameraSettings, cameraSettings } from '../render/cameraSettings'; // +camera
import { cameraSignals, followFacing, syncCameraSignals } from '../input/cameraSignals'; // +camera
import { CursorFeedback } from './cursorFeedback'; // +camera
import { unitColor } from '../render/palette';
import { createPlaceholderRenderer } from '../render/placeholder/index';
import type { AnimCue, Renderer, RenderFx, RenderUnit } from '../render/types';
import type { GraphicsSettings } from '../render/settings';
import { type PoseMap, buildFrame, capturePoses, castCueKind, eventsToCues, eventsToFx } from './frame';
import { LocalMotion } from './localMotion';
import { METER_MODES, type MeterMode, type MeterRow, type MeterScope, MeterTracker, type ScoreRow, meterRows, scoreboard } from './meters';
import { type CastPress, PlayerController, SHOWN_ERRORS } from './player';
import { CastUx, isPlacing } from './castux'; // +castux: ground placement, cast ring, error text
import { PREDICTION_TTL_MS, localFeedback, predictPress, pressFormId, reconcile, resetLocalFeedback } from './prediction';
import { CommandLog, TickDriver } from './tickDriver';
import { LatencyProbe, latencyProbeRequested } from './latencyProbe';
import { perf, perfRequested } from '../debug/perf';
import { AutoQuality } from '../render/autoQuality';
import { MechanicsCollector } from '../render/toon/fx/mechanics/model';
import type { SessionHooks } from '../tutorial/hooks'; // +tutorial
import type { NetSession } from '../net/netSession'; // +multiplayer
import { OwnPredictor } from '../net/predict'; // +multiplayer
import { sameCommand } from '../net/protocol'; // +multiplayer
import { presenceHook } from '../net/presenceHook'; // +presence: other players in a shared field (net/fieldPresence.ts)

export interface SessionEnd {
  result: RunResult;
  dungeonName: string;
  rows: Record<MeterMode, MeterRow[]>;
  /** End-of-run scoreboard (whole run, party order). */
  scoreboard?: ScoreRow[];
  /** Kill XP/gold summed from the run's enemy deaths (progression killRewardsFromEvents); null if it failed. */
  kills: KillRewardSummary | null;
}

export interface SessionOptions {
  canvasHost: HTMLElement;
  hudHost: HTMLElement;
  content: ContentRegistry;
  /** Class ids in slot order. */
  party: readonly string[];
  /** Unit names in slot order (hero names); default: the class name. */
  partyNames?: readonly string[];
  /** Main hero + purse at run start (meta/inrunTally inRunStart) for the in-run XP bar and gold counter. */
  progress?: InRunStart | null;
  config: StartConfig;
  onEnd(end: SessionEnd): void;
  /** Swap in another renderer (e.g. the stylized one) without touching the game layer. */
  createRenderer?: (host: HTMLElement) => Renderer;
  /** Graphics settings handed to renderers that support them. */
  settings?: GraphicsSettings;
  /** +world-resources: unit that breaks clutter / collects pickups (default: the human; spectate: the main class's bot). */
  looterId?: UnitId | null;
  /** +tutorial: observer / gate hooks (guided tutorial, run coach). See tutorial/hooks.ts. */
  hooks?: SessionHooks;
  /**
   * +multiplayer: play inside a party room (net/netSession.ts). The local sim only replays the
   * room's frames (bots run in the room), own commands go to the room, pause is off.
   */
  net?: NetSession;
  /**
   * +personas: the simulated players behind the bot slots (matched by class; content/personas).
   * Default (undefined): three companions generated from the run seed (bots/persona/roster.ts
   * defaultCompanions). null: plain bots, no chat. Ignored in a party room (the room runs the bots
   * and sends their names).
   */
  personas?: readonly Persona[] | null;
  /** +personas: default 'companion' when a human plays, 'unhinged' when every slot is a bot (spectate). */
  personaMode?: PersonaMode;
  /** +field: open-field / levelled play (world/worldApp.ts). Absent = a plain M+ key. */
  field?: SessionField;
}

/**
 * +field: what the world layer (world/worldApp.ts) plugs into a session: party levels and the
 * arrival point, every step's events (portals, deaths, level-ups), the Auto brain that drives the
 * player's hero, and a hook for manual input (which turns Auto off, like WoW auto-run).
 */
export interface SessionField {
  /** Party levels in slot order (PartyMemberConfig.level); absent entries stay unlevelled. */
  levels?: readonly (number | undefined)[];
  /** Where the party arrives (SimConfig.arrive). */
  arrive?: SimConfig['arrive'];
  /** Every sim step's events, after the step. */
  onEvents?(events: readonly SimEvent[], world: DeepReadonly<World>): void;
  /** Auto mode: the brain that plays the human's hero this tick (null = the player does). */
  autoBrain?(): BotBrain | null;
  /** The player moved or pressed a cast / dodge key while `autoBrain` was driving. */
  onManual?(kind: 'move' | 'cast'): void;
  /** No pause key (P) on this map. */
  noPause?: boolean;
  /** Start in the open-world chase framing (fields and town; a dungeon party keeps the high view). */
  chaseCamera?: boolean;
}

export interface Session {
  readonly sim: Sim;
  /** Current renderer (debug / perf stats). */
  readonly renderer: Renderer;
  readonly rig: CameraRig;
  /** Hot-swap the renderer (settings panel); the sim keeps running. */
  setRenderer(factory: (host: HTMLElement) => Renderer): void;
  applySettings(settings: GraphicsSettings): void;
  /**
   * Dev/debug only: end the run now as completed (timed by default), as if the last boss died.
   * The end screen gets the live kill tally and a result with full forces.
   */
  debugFinish(opts?: { timed?: boolean }): void;
  /** The human's commands by the tick that applied them (replays: see tickDriver.replayLog). */
  readonly commandLog: CommandLog;
  dispose(): void;
  /** +personas: say a line in party chat as the player (no-op when spectating). */
  say(text: string): void;
  /** +field: the human's unit id (null when spectating). */
  readonly playerId: UnitId | null;
  /** +field: queue a command for the next sim tick (e.g. `setLevel`); logged with the player's. */
  command(cmd: Command): void;
}

const END_DELAY_MS = 2000;
/** +multiplayer: the predicted own path's velocity is measured over this much ahead of the present. */
const OWN_VEL_MS = 10;
const SPEEDS = [1, 2, 4, 8];

export function startSession(opts: SessionOptions): Session {
  const { content, config } = opts;
  const dungeon = content.dungeon(config.dungeonId);
  const personas = opts.net ? [] : sessionPersonas(opts);
  const personaOf = (i: number): Persona | undefined => {
    const classId = opts.party[i];
    return i === config.slot || classId === undefined ? undefined : personas.find((p) => p.classId === classId);
  };
  const simConfig: SimConfig = {
    seed: config.seed,
    content,
    dungeonId: config.dungeonId,
    keystone: { level: config.level, affixes: config.affixes === 'auto' ? affixesForLevel(config.level) : config.affixes },
    party: opts.party.map((classId, i) => ({
      classId,
      name: personaOf(i)?.name ?? opts.partyNames?.[i] ?? content.class(classId)?.name ?? classId, // +personas: bots go by their player's name
      controller: opts.net ? (opts.net.controllers[i] ?? ('bot' as const)) : i === config.slot ? ('human' as const) : ('bot' as const),
      ...(opts.field?.levels?.[i] !== undefined ? { level: opts.field.levels[i] } : {}), // +field
    })),
    ...(opts.looterId !== undefined ? { looterId: opts.looterId } : {}), // +world-resources
    ...(opts.field?.arrive ? { arrive: opts.field.arrive } : {}), // +field
  };
  const sim = createSim(simConfig);
  const world = (): World => sim.getWorld() as World;
  const playerId: UnitId | null = config.slot >= 0 ? (world().partyIds[config.slot] ?? null) : null;
  // +multiplayer: own-hero prediction in a party run (rewind to the confirmed sim, replay unacked inputs).
  if (opts.net && playerId) opts.net.predictor = new OwnPredictor(createSim({ ...simConfig, looterId: null }), playerId);
  const spectating = playerId === null;

  let bots: BotBrain[] = [];
  const net = opts.net ?? null; // +multiplayer
  try {
    const mode: PersonaMode = opts.personaMode ?? (playerId ? 'companion' : 'unhinged');
    if (!net) bots = createPartyBots({ world: world() }, config.preset, config.seed, personas.length ? { personas, mode } : {});
  } catch (err) {
    console.warn('[web] bots unavailable, party members will idle', err);
  }
  const botErrors = new Set<UnitId>();

  let settings = opts.settings;
  /** Frame-time watchdog: steps the quality preset down while frames stay over budget (not saved). */
  const autoQ = new AutoQuality(settings?.quality ?? 'high');
  /** `?autoquality=0` pins the preset (profiling a fixed preset). */
  const autoAllowed = typeof location === 'undefined' || new URLSearchParams(location.search).get('autoquality') !== '0';
  const applyQuality = (): void => {
    if (settings) renderer.applySettings?.(autoQ.current === settings.quality ? settings : { ...settings, quality: autoQ.current });
  };
  let renderer = (opts.createRenderer ?? createPlaceholderRenderer)(opts.canvasHost);
  renderer.init(dungeon);
  if (settings) renderer.applySettings?.(settings);
  const rig = new CameraRig();
  const unbindCamera = bindCameraSettings(rig); // +camera: saved zoom + follow style
  // +field: open maps start in the WoW-style chase framing (lower, closer: the horizon and the Tower
  // show) unless the player picked a zoom; a pitch they dragged carries over between maps.
  if (opts.field?.chaseCamera) applyWorldFraming(rig);
  const cursorFx = new CursorFeedback(opts.canvasHost, content, sim.queries); // +camera: cursor by what's under it
  rig.state.yaw = opts.field?.arrive?.facing ?? dungeon?.entranceFacing ?? Math.PI / 2; // +field: behind the arriving hero
  const followId = (): UnitId | null => playerId ?? world().partyIds[0] ?? null;
  const start = followId() ? world().units[followId()!]?.pos : undefined;
  if (start) rig.state.focus = { x: start.x, y: start.y };

  const player = playerId ? new PlayerController(playerId, content, sim.queries) : null;
  if (player && opts.net) player.waitOutForcedMoves = true; // +multiplayer: see PlayerController
  if (player && playerId) {
    // Tab candidates know the camera (on screen, distance from the screen centre) and walls.
    const los = walkableLos((p) => {
      try {
        return sim.queries.spatial.isWalkable(world(), p);
      } catch {
        return true;
      }
    });
    const view: TargetingView = {
      project: (p, h) => renderer.project(p, h),
      aim: { x: 0, y: 0 },
      radius: 1,
    };
    player.targeting = (w, o) => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      view.aim.x = vw / 2;
      view.aim.y = vh / 2;
      view.radius = Math.hypot(vw, vh) / 2;
      return targetCandidates(w, playerId, { view, los, frontOnly: o.frontOnly });
    };
    player.clock = () => performance.now();
    if (import.meta.env.DEV) (window as unknown as { __tabPick?: () => unknown }).__tabPick = () => player.selectedId;
  }
  let spectatorSelected: UnitId | null = null;
  const selected = (): UnitId | null => player?.selectedId ?? spectatorSelected;
  const select = (id: UnitId | null): void => {
    if (player) player.pick(id);
    else spectatorSelected = id;
  };
  /** +autoattack: a living unit hostile to the player's hero. */
  const isEnemy = (id: UnitId): boolean => {
    const w = world();
    const me = playerId ? w.units[playerId] : undefined;
    const u = w.units[id];
    return !!me && !!u && u.alive && u.faction !== me.faction;
  };

  const hud = new Hud(opts.hudHost, {
    bind: (b) => pressBind(b),
    dodge: () => pressDodge(),
    select: (id) => select(id),
    release: () => player?.releaseSpirit(),
    say: (text) => say(text), // +personas
  });
  if (settings) hud.setFxIntensity(settings.fxIntensity);
  // +castux: placement / cast ring / error text glue (game/castux.ts); the crosshair cursor reads isPlacing.
  const castux = player && playerId ? new CastUx({ content, queries: sim.queries, player, playerId, world, onError: (t) => hud.showError(t, performance.now()) }) : null;
  cursorFx.isPlacing = isPlacing; // +castux
  if (player) player.onError = (text) => hud.showError(castux ? castux.errorText(text) : text, performance.now()); // +castux: names the resource
  // Kill rewards, tallied per tick while the dead units are still in the world (they may despawn
  // later) with one running carry, so the in-run pops add up to exactly what applyRunResult grants.
  const tally = new InRunTally(content, config.level, opts.progress ?? null);
  hud.beginProgress(opts.progress ? tally : null);

  const driver = new TickDriver(() => (probe ? probe.time('tick', tick) : tick()), {
    stepMs: TICK_MS,
    now: () => performance.now(),
    scheduler: { set: (fn, ms) => setTimeout(fn, ms), clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>) },
  });
  const acc = driver.acc;
  const commandLog = new CommandLog();
  const motion = new LocalMotion();
  localFeedback.actorId = playerId;
  localFeedback.prediction = null;
  // +controls: control scheme (camera-relative / WoW), autorun and the client-steered facing.
  const steering = new Steering();
  const unsubControls = controlSettings.subscribe((c) => {
    steering.scheme = c.scheme;
    localControl.scheme = c.scheme;
    // Action controls (WoW): standing still nothing turns the hero but a cast / auto-attack start.
    if (player) player.autoFace = c.scheme !== 'action';
  });
  /** +glide: Space as jump / glide (input/glideHold.ts), and the own unit as a command sent now finds it. */
  const glideHold = new GlideHold();
  const ownUnit = () => (playerId ? (net ? net.view(world()) : world()).units[playerId] : undefined);
  /** Current move direction (sim coordinates) from the held keys, mouse buttons and autorun. */
  const heldDir = () => steering.moveDir(input.heldMoves, input.buttons, rig.state.yaw);
  /** WoW controls: the facing to send with the move (undefined: camera-relative, face the run). */
  const steerIntent = () => (steersFacing(steering.scheme) ? { facing: steering.facingIntent(input.buttons) } : undefined);
  /** Commands are waiting: run the next tick now (or as soon as allowed) instead of on the frame loop. */
  const requestTick = (): void => {
    if (!player || disposed || paused) return;
    // +multiplayer: commands go to the room now; the room's frame brings them back.
    if (net) return void net.flush(player.drain(net.view(world()), heldDir(), steerIntent()));
    driver.input(() => !disposed && !paused && player.hasWork(world(), heldDir(), steerIntent()));
  };
  const probe = typeof location !== 'undefined' && latencyProbeRequested(location.search) ? new LatencyProbe(opts.hudHost) : null;
  if (probe) (window as unknown as { __latency?: LatencyProbe }).__latency = probe;
  if (typeof location !== 'undefined' && perfRequested(location.search)) {
    perf.enabled = true;
    perf.attachOverlay(opts.hudHost);
    perf.reset();
    (window as unknown as { __perf?: typeof perf }).__perf = perf;
  }
  /** GCD-sweep state of each action button at press time (probe only: a sweep already running is not feedback). */
  const gcdAtPress = new Map<string, boolean>();
  /** Selection when Tab went down (probe only). */
  let selAtTab: UnitId | null = null;
  let lastPlayerPos: { x: number; y: number } | null = null;
  let paused = false;
  let speedIdx = 0;
  const input = attachDomInput(opts.canvasHost, rig, (x, y) => renderer.pickUnit(x, y), {
    ability: (b) => pressBind(b),
    dodge: () => pressDodge(),
    jump: () => {
      if (!player || disposed) return;
      if (autoDriving) hookOk(() => opts.field?.onManual?.('move'), undefined); // +field: jumping takes over from Auto
      // +glide: on the ground Space jumps; mid-air it opens the glider (held past the apex: see steer()).
      if (glideHold.press(!!ownUnit()?.jump)) player.glide(true);
      else player.jump();
      requestTick();
    },
    jumpUp: () => {
      if (!player || disposed) return;
      if (glideHold.release(!!ownUnit()?.jump?.air?.glide)) {
        player.glide(false);
        requestTick();
      }
    },
    tabTarget: (rev) => {
      if (player) player.tabTarget(world(), rev);
      else select(nextTabTarget(world(), world().partyIds[0] ?? '', spectatorSelected, rev));
      requestTick();
    },
    partyTarget: (slot) => {
      select(world().partyIds[slot] ?? null);
      requestTick();
    },
    escape: () => {
      if (castux?.escape()) return; // +castux: Esc cancels ground placement first
      if (player) player.escape(world());
      else select(null);
      requestTick();
    },
    clickUnit: (id, button) => {
      // +castux: while placing, left click confirms at the cursor and right click cancels.
      const ux = castux?.click(button);
      if (ux?.handled) {
        if (ux.press) predict(ux.press);
        return requestTick();
      }
      // +autoattack: right-click on a hostile targets it and starts auto-attack (WoW interact).
      if (id && button === 'right' && player && isEnemy(id)) player.attackUnit(world(), id);
      else if (id) select(id);
      requestTick();
    },
    // +autoattack: the Attack key (Controls settings; unbound by default) toggles auto-attack.
    attack: () => {
      if (!player || disposed) return;
      if (autoDriving) hookOk(() => opts.field?.onManual?.('cast'), undefined);
      player.toggleAttack(world());
      requestTick();
    },
    attackKey: () => controlSettings.get().attackKey,
    turnKeys: () => ({ left: controlSettings.get().turnLeftKey, right: controlSettings.get().turnRightKey }), // +controls
    moveChanged: () => requestTick(),
    // +controls
    moveKeyDown: (k) => steering.keyDown(k),
    autorun: () => {
      if (autoDriving) hookOk(() => opts.field?.onManual?.('move'), undefined); // +field
      steering.toggleAutorun();
      requestTick();
    },
    buttonsChanged: (b) => {
      steering.buttonsChanged(b);
      requestTick();
    },
    lockOnDrag: () => steersFacing(steering.scheme),
    // +castux: while placing, mouse presses confirm (left) / cancel (right) instead of drag / select.
    isPlacing: () => isPlacing(),
    placementClick: (button) => {
      const ux = castux?.click(button);
      if (ux?.handled && ux.press) predict(ux.press);
      requestTick();
    },
    other: (code) => {
      if (code === 'KeyP' && !net && !opts.field?.noPause) {
        paused = !paused;
        // Presses while paused are dropped, not piled up for the first tick after unpausing.
        if (player) player.acceptIntents = !paused;
      }
      if (spectating && (code === 'Equal' || code === 'NumpadAdd')) speedIdx = Math.min(SPEEDS.length - 1, speedIdx + 1);
      if (spectating && (code === 'Minus' || code === 'NumpadSubtract')) speedIdx = Math.max(0, speedIdx - 1);
    },
    probe: probe
      ? (kind, key, t0) => {
          if (kind === 'ability') gcdAtPress.set(key, !!actionSlot(key)?.querySelector('.action-gcd.on'));
          if (kind === 'tab') selAtTab = selected();
          probe.press(kind, key, t0, performance.now());
        }
      : undefined,
  });
  const actionSlot = (bind: string): Element | undefined => opts.hudHost.querySelectorAll('.actionbar .action')[UI_BIND_KEYS.indexOf(bind as never)];

  /** An action key / button press: pressed visual now, command + prediction, then an early tick. */
  function pressBind(bind: UiBind): void {
    if (probe) probe.time('press', () => pressBindNow(bind));
    else pressBindNow(bind);
  }
  function pressBindNow(bind: UiBind): void {
    if (!player || disposed) return;
    if (autoDriving) hookOk(() => opts.field?.onManual?.('cast'), undefined); // +field: a cast key ends Auto
    if (!hookOk(() => opts.hooks?.allowBind?.(bind) ?? true, true)) return; // +tutorial: locked bind
    flashActionBind(bind, performance.now());
    const ux = castux ? castux.pressBind(bind) : null; // +castux: ground placement (enter / confirm)
    const press = ux?.handled ? ux.press : player.castBind(world(), bind);
    if (press) predict(press);
    requestTick();
  }

  /** Space / the Dodge button: dash along the held move keys (else forward). */
  function pressDodge(): void {
    if (!player || disposed) return;
    if (autoDriving) hookOk(() => opts.field?.onManual?.('cast'), undefined); // +field
    if (!hookOk(() => opts.hooks?.allowDodge?.() ?? true, true)) return; // +tutorial: locked Dodge
    flashDodge(performance.now());
    // +multiplayer: dash from where the own hero is drawn (the predicted present), not the confirmed past.
    // +controls: no keys held, the dash goes where the hero faces now (the client-steered facing).
    const press = player.dodge(world(), heldDir(), net?.ownAt(0) ?? undefined, steersFacing(steering.scheme) ? steering.facing : undefined);
    if (press) predict(press);
    requestTick();
  }

  /** Predict the press's visuals with the sim's own queries (canCast, castTiming). */
  function predict(press: CastPress): void {
    if (!playerId || !press.check) return;
    const w = world();
    const u = w.units[playerId];
    // A transformed button predicts its form (own cast time, GCD, cooldown); the command keeps the pressed id.
    const ab = content.ability(pressFormId(content, w, playerId, press.abilityId));
    if (!u || !ab) return;
    try {
      const haste = sim.queries.effectiveStats(playerId).haste;
      const check = press.check;
      let busyLeft = 0;
      if (!check.ok) {
        if (check.reason === 'on_gcd') busyLeft = u.gcdUntil - w.time;
        else if (check.reason === 'casting') busyLeft = u.cast ? u.cast.endsAt - w.time : 0;
        else if (check.reason === 'on_cooldown') busyLeft = sim.queries.cooldownRemaining(playerId, ab.id);
      }
      const p = predictPress({
        world: w,
        actorId: playerId,
        abilityId: ab.id,
        pressedId: press.abilityId,
        targetId: press.targetId,
        check,
        busyLeft,
        timing: castTiming(content, u, ab, haste),
        interruptible: ab.interruptible ?? true,
        presentMs: w.time + driver.sinceStepMs,
        realNow: performance.now(),
        command: press.command,
      });
      if (!p) return;
      localFeedback.prediction = p;
      if (p.state !== 'start') return;
      if (p.cast) {
        // Charge-up FX start on the press (the toon renderer keys them off begincast); the sim's own begincast is skipped.
        pendingEvents.push({ type: 'begincast', sourceID: playerId, targetID: p.cast.targetId ?? undefined, abilityGameID: ab.id, duration: p.cast.endsAt - p.cast.startedAt, interruptible: p.cast.interruptible, timestamp: w.time } as SimEvent);
        predictedBegin = p;
      } else {
        // The ability clip starts on the press; a rejection sends a `cancel` cue.
        pendingCues.push({ unitId: playerId, kind: castCueKind(content, ab.id), abilityId: ab.id });
        p.cued = true;
      }
    } catch (err) {
      console.warn('[web] cast prediction failed (visual only)', err);
    }
  }
  /** A prediction whose synthetic begincast went to the renderer. */
  let predictedBegin: ReturnType<typeof predictPress> = null;

  const tracker = new MeterTracker();

  // +tutorial: session hooks (tutorial/hooks.ts). A throwing hook is logged once and ignored.
  let hookWarned = false;
  function hookOk<T>(fn: () => T, fallback: T): T {
    try {
      return fn();
    } catch (err) {
      if (!hookWarned) console.warn('[web] session hook failed', err);
      hookWarned = true;
      return fallback;
    }
  }
  hookOk(() => opts.hooks?.attach?.({ content, queries: sim.queries, playerId, hudHost: opts.hudHost, project: (p, h) => renderer.project(p, h ?? 0), selectedId: () => selected() }), undefined);
  const colorOf = (id: UnitId): string => {
    const u = world().units[id];
    return u ? unitColor(u) : '#888';
  };
  const rowsFor = (mode: MeterMode, scope: MeterScope = 'overall'): MeterRow[] => meterRows(world(), mode, tracker, sim.log, colorOf, scope);

  let poses: PoseMap = capturePoses(world());
  let pendingFx: RenderFx[] = [];
  let pendingCues: AnimCue[] = [];
  let pendingEvents: SimEvent[] = [];
  let ccIds = new Set<UnitId>();
  let stealthIds = new Set<UnitId>(); // +basekits: Lurk fades the hero
  const schoolOf = (abilityId: string) => content.ability(abilityId)?.school ?? null;
  const mechanics = new MechanicsCollector(content); // +encounters: enemy-mechanic overlay marks
  let fxSeq = 0;
  let endedAt: number | null = null;
  let disposed = false;
  let forced: Partial<RunResult> | null = null;
  // +personas: last step's events (bots read killing blows / chat from them), social events for the HUD, the player's chat.
  let lastEvents: SimEvent[] = [];
  let pendingSocial: SimEvent[] = [];
  let saying: Command[] = [];
  // +field: world commands for the next tick, and whether the Auto brain drove the last one.
  let queued: Command[] = [];
  let autoDriving = false;
  const autoBrain = (): BotBrain | null => (opts.field?.autoBrain ? hookOk(() => opts.field!.autoBrain!(), null) : null);

  function tick(): void {
    // +multiplayer: replay the room's next frame; stall until one arrives.
    const netFrame = net ? net.next(sim) : null;
    if (net && !netFrame) return;
    const w = world();
    poses = capturePoses(w);
    const cmds: Command[] = [];
    let mine: Command[] = [];
    // (In a party run the own commands are drained after the step, against the fresh prediction.)
    // +field: Auto plays the hero with a bot brain; moving by hand hands control back (WoW auto-run).
    let brain = player && !net ? autoBrain() : null;
    if (brain) {
      const d = heldDir();
      if (d.x !== 0 || d.y !== 0) {
        hookOk(() => opts.field?.onManual?.('move'), undefined);
        brain = autoBrain();
      }
    }
    if (player && !net && brain && playerId) {
      try {
        mine = brain.decide({ world: w, selfId: playerId, content, queries: sim.queries, now: w.time, events: lastEvents }, w.time);
      } catch (err) {
        if (!botErrors.has(playerId)) console.warn('[web] auto brain threw; continuing', err);
        botErrors.add(playerId);
      }
      cmds.push(...mine);
      autoDriving = true;
    } else if (player && !net) {
      // Auto just ended: stop wherever the brain was walking (the held keys take over from here).
      if (autoDriving) {
        autoDriving = false;
        mine.push({ type: 'move', actorId: player.actorId, dir: { x: 0, y: 0 } });
      }
      mine.push(...player.drain(w, heldDir(), steerIntent()));
      cmds.push(...mine);
    }
    // +field: queued world commands (setLevel) go in with this tick's.
    if (queued.length) {
      cmds.push(...queued);
      mine.push(...queued);
      queued = [];
    }
    if (netFrame) {
      cmds.push(...netFrame.c);
      mine = netFrame.c.filter((c) => c.actorId === playerId);
    }
    // +personas: the player's chat lines go in with this tick's commands.
    if (saying.length && !net) {
      cmds.push(...saying);
      mine.push(...saying);
    }
    saying = [];
    perf.begin('tick.bots');
    for (const b of bots) {
      try {
        const out = b.decide({ world: w, selfId: b.unitId, content, queries: sim.queries, now: w.time, events: lastEvents }, w.time);
        // +tutorial: the director holds bots (or their kicks) back until a lesson needs them.
        cmds.push(...(opts.hooks?.filterBotCommands ? hookOk(() => opts.hooks!.filterBotCommands!(b.unitId, out, w), out) : out));
      } catch (err) {
        if (!botErrors.has(b.unitId)) console.warn(`[web] bot ${b.unitId} threw; continuing`, err);
        botErrors.add(b.unitId);
      }
    }
    perf.end('tick.bots');
    let events: SimEvent[] = [];
    try {
      perf.begin('tick.sim');
      events = sim.step(cmds);
      perf.end('tick.sim');
      if (net && netFrame) {
        net.check(netFrame, w);
        net.predict(w);
        // Compare the held keys with the own hero as a command sent now will find it (net/predict.ts).
        if (player) net.flush(player.drain(net.view(w), heldDir(), steerIntent()));
      }
      if (probe && playerId) {
        const now = performance.now();
        if (mine.some((c) => c.type === 'cast')) probe.mark('accept', now, (k) => k === 'ability');
        if (mine.some((c) => c.type === 'move')) probe.mark('accept', now, (k) => k === 'move');
      }
    } catch (err) {
      console.error('[web] sim.step failed', err);
      paused = true;
      if (player) player.acceptIntents = false;
      hud.showError('Simulation error (see console). Paused.', performance.now());
      return;
    }
    lastEvents = events;
    if (player && !autoDriving) player.swingFeedback(w, performance.now()); // +autoattack: facing error
    // +field: Auto's target shows as the player's (target frame, ring); the world sees every step's events.
    if (autoDriving && player && playerId) player.selectedId = w.units[playerId]?.targetId ?? null;
    if (opts.field?.onEvents) hookOk(() => opts.field!.onEvents!(events, w), undefined);
    for (const e of events) if (e.type === 'chat' || e.type === 'presence' || e.type === 'party_leave') pendingSocial.push(e); // +personas
    commandLog.record(w.tick, mine);
    tracker.ingest(events, w, TICK_MS);
    pendingFx.push(...eventsToFx(events, content, w.time - TICK_MS, () => ++fxSeq));
    // Reconcile the predicted press with what the sim did on the tick that applied it.
    const pred = localFeedback.prediction;
    let skipCue: SimEvent | null = null;
    let skipBegin: SimEvent | null = null;
    if (pred) {
      const r = reconcile(pred, w, events, mine.some((c) => sameCommand(c, pred.command)));
      if (r.status !== 'pending') localFeedback.prediction = null;
      if (r.status === 'confirmed') {
        if (pred.cued) skipCue = events.find((e) => e.type === 'cast' && e.sourceID === pred.actorId && e.abilityGameID === pred.abilityId) ?? null;
        if (predictedBegin === pred) skipBegin = events.find((e) => e.type === 'begincast' && e.sourceID === pred.actorId && e.abilityGameID === pred.abilityId) ?? null;
      } else if (r.status === 'rejected') {
        // Cancel the predicted visuals and say why.
        if (predictedBegin === pred) pendingEvents.push({ type: 'cast_cancelled', sourceID: pred.actorId, abilityGameID: pred.abilityId, reason: 'player', timestamp: w.time } as SimEvent);
        if (pred.cued) pendingCues.push({ unitId: pred.actorId, kind: 'cancel', abilityId: pred.abilityId });
        const again = safeCanCast(pred.pressedId ?? pred.abilityId);
        const msg = again && !again.ok ? (castux ? castux.errorFor(again.reason, pred.pressedId ?? pred.abilityId) : SHOWN_ERRORS[again.reason]) : undefined; // +castux
        if (msg) hud.showError(msg, performance.now());
      }
      if (r.status !== 'pending' && predictedBegin === pred) predictedBegin = null;
    }
    pendingCues.push(...eventsToCues(skipCue ? events.filter((e) => e !== skipCue) : events, content));
    for (const e of events) if (e !== skipBegin) pendingEvents.push(e);
    hookOk(() => opts.hooks?.onTick?.(events, w), undefined); // +tutorial
    const paid = tally.ingest(events, w);
    if (paid) hud.rewards(paid, performance.now());
    // Sample crowd control once per tick for stun/CC animations.
    const cc = new Set<UnitId>();
    const stealth = new Set<UnitId>();
    for (const id of w.unitOrder) {
      const u = w.units[id];
      if (!u?.alive || u.auras.length === 0) continue;
      try {
        if (!sim.queries.controlState(id).canAct) cc.add(id);
      } catch {
        /* cosmetic only */
      }
      if (u.kind === 'player' && u.auras.some((a) => sim.content.aura(a.auraId)?.flags?.stealth)) stealth.add(id); // +basekits
    }
    ccIds = cc;
    stealthIds = stealth;
  }

  function safeCanCast(abilityId: string) {
    try {
      return playerId ? sim.queries.canCast(playerId, abilityId, world().units[playerId]?.targetId ?? null, null) : null;
    } catch {
      return null;
    }
  }

  let raf = 0;
  let zoneChannel: string | null = null; // +presence
  let last = performance.now();
  driver.advanceTo(last);
  let warmed: Renderer | null = null;
  function frame(t: number): void {
    if (disposed) return;
    const cpu0 = performance.now();
    perf.frameBegin(cpu0);
    const dt = t - last;
    last = t;
    acc.timeScale = paused ? 0 : SPEEDS[speedIdx]!;
    steer(paused ? 0 : dt);
    perf.begin('tick');
    driver.advanceTo(t);
    // +multiplayer: frames beyond the jitter reserve (late start, a hitch) are replayed at once.
    for (let n = 0; net && net.backlog() > 0 && n < 200; n++) tick();
    perf.end('tick');

    const w = world();
    // The sim's "present": latest tick time + time since it (negative right after an early tick).
    const present = w.time + Math.min(TICK_MS, driver.sinceStepMs);
    localFeedback.presentMs = present;
    const pred = localFeedback.prediction;
    if (pred && t - pred.pressedAt > PREDICTION_TTL_MS) {
      localFeedback.prediction = null;
      if (pred.cued) pendingCues.push({ unitId: pred.actorId, kind: 'cancel', abilityId: pred.abilityId });
      if (predictedBegin === pred) {
        pendingEvents.push({ type: 'cast_cancelled', sourceID: pred.actorId, abilityGameID: pred.abilityId, reason: 'player', timestamp: w.time } as SimEvent);
        predictedBegin = null;
      }
    }
    perf.begin('pick');
    const cursor = input.cursor;
    const worldHover = cursor ? renderer.pickUnit(cursor.x, cursor.y) : null;
    const hoverId = hud.frameHoverId ?? worldHover;
    if (player) {
      player.hoverId = hoverId;
      player.cursorPoint = cursor ? renderer.pickGround(cursor.x, cursor.y) : null;
    }
    cursorFx.update(w, playerId, worldHover, player?.cursorPoint ?? null); // +camera
    perf.end('pick');
    perf.begin('buildFrame');
    const view = { playerId, selectedId: selected(), hoverId, ccIds, stealthIds, schoolOf };
    const f = buildFrame(w, poses, acc.alpha, view, pendingFx);
    f.cues = pendingCues;
    f.events = pendingEvents;
    f.mechanics = mechanics.collect(w, f.timeMs);
    pendingFx = [];
    pendingCues = [];
    pendingEvents = [];
    if (playerId) showLocalPlayer(f.units.find((u) => u.id === playerId), w, present, dt);
    const zone = presenceHook.current; // +presence: other players (units, VFX events, cues) join the frame
    if (zone) hookOk(() => zone.decorate(f, content, t), undefined);
    const unitsById = new Map<UnitId, RenderUnit>(f.units.map((u) => [u.id, u]));
    castux?.decorate(f, playerId ? unitsById.get(playerId) : undefined, player?.cursorPoint ?? null, present, t); // +castux: placement circle + cast ring
    const fid = followId();
    const followUnit = fid ? unitsById.get(fid) : undefined;
    // +camera: facing + input edges drive the optional follow return (input/cameraSignals.ts).
    syncCameraSignals(input.heldMoves);
    if (followUnit) rig.follow(followUnit.pos, dt, followUnit.id === playerId ? followFacing(followUnit.facing) : followUnit.facing, cameraSignals);
    perf.end('buildFrame');
    perf.begin('render');
    if (probe) probe.time('render', () => renderer.render(f, rig, dt));
    else renderer.render(f, rig, dt);
    perf.end('render');
    // Pre-warm once per renderer, after its first frame built the unit views: compile every
    // shader / material and draw each effect type once off-screen so the first cast can't hitch.
    if (warmed !== renderer) {
      warmed = renderer;
      try {
        renderer.prewarm?.();
      } catch (err) {
        console.warn('[web] renderer prewarm failed', err);
      }
    }
    // Floating text follows the visuals: the toon renderer holds in-flight hits until they land,
    // so crit text, hit-stop and shake all fire on the same frame.
    perf.begin('hud');
    if (pendingSocial.length) {
      const social = pendingSocial;
      pendingSocial = [];
      hud.social(social, w, t); // +personas
    }
    if (zone || zoneChannel) hookOk(() => hud.zoneChat(zone ? zone.drainChat() : [], t, (zoneChannel = zone ? 'Zone' : null)), undefined); // +presence: zone chat
    const textEvents = renderer.combatTextEvents?.() ?? f.events;
    if (textEvents && textEvents.length) hud.ingest(textEvents, playerId, t);

    const status = paused ? 'Paused (P)' : spectating ? `Spectating • ${SPEEDS[speedIdx]}x (+/-)` : '';
    const hudUpdate = (): void => hud.update({ world: w, content, queries: sim.queries, playerId, selectedId: selected(), hoverId, frame: f, unitsById, renderer, focus: rig.state.focus, meterRows: rowsFor, status }, t);
    if (probe) {
      probe.time('hud', hudUpdate);
      probeFrame(f, dt);
    } else hudUpdate();
    if (zone) hookOk(() => zone.overlay(renderer, rig.state.focus, t), undefined); // +presence: nameplates
    perf.end('hud');
    hookOk(() => opts.hooks?.onFrame?.(t, w), undefined); // +tutorial
    if (settings?.autoQuality && autoAllowed && !paused && autoQ.sample(performance.now() - cpu0, dt)) {
      applyQuality();
      console.info(`[web] auto quality → ${autoQ.current} (${autoQ.reason})`);
    }
    if (perf.enabled) perf.notes['quality'] = `${autoQ.current}${settings?.autoQuality ? ' (auto)' : ''}${autoQ.reason ? ` · ${autoQ.reason}` : ''}`;
    perf.frameEnd(dt);

    const phase = w.mplus.phase;
    if ((phase === 'completed' || phase === 'depleted') && endedAt === null) endedAt = t;
    if (forced || (endedAt !== null && t - endedAt > END_DELAY_MS)) {
      // The end screen owns the keyboard now (Tab focus between its buttons).
      input.enabled = false;
      const rows = Object.fromEntries(METER_MODES.map((m) => [m, rowsFor(m)])) as Record<MeterMode, MeterRow[]>;
      const result = forced ? { ...sim.result(), ...forced } : sim.result();
      opts.onEnd({ result, dungeonName: dungeon?.name ?? config.dungeonId, rows, scoreboard: scoreboard(world(), tracker, sim.log, colorOf, playerId ?? null), kills: tally.summary() });
      return;
    }
    raf = requestAnimationFrame(frame);
  }

  /** +controls: advance the steered facing (WoW turn keys / mouselook) before this frame's ticks drain it. */
  function steer(dtMs: number): void {
    const w0 = world();
    const own = playerId ? (net ? net.view(w0) : w0).units[playerId] : undefined;
    // +glide: Space still down past the hop's apex opens the glider.
    if (player && glideHold.isHeld && glideHold.poll(airView(own, w0.time))) {
      player.glide(true);
      requestTick();
    }
    let canTurn = !!own?.alive;
    try {
      if (own && canTurn) canTurn = sim.queries.controlState(own.id).canAct;
    } catch {
      /* cosmetic only */
    }
    if (own) steering.update(dtMs, input.heldMoves, input.buttons, rig.state.yaw, own.facing, canTurn);
    const dir = heldDir();
    localControl.steering = steering.isSteering(input.buttons);
    localControl.turning = turnAxis(steering.scheme, input.heldMoves, input.buttons) !== 0;
    localControl.dragging = input.buttons.left || input.buttons.right;
    localControl.autorun = steering.autorun;
    localControl.moving = dir.x !== 0 || dir.y !== 0;
    localControl.forward = forwardAxis(steering.input(input.heldMoves, input.buttons, rig.state.yaw)) > 0;
    localControl.facing = own ? steering.facing : null;
  }

  /** Draw the local player at the present: predicted movement from held keys, predicted / sub-tick cast. */
  function showLocalPlayer(me: RenderUnit | undefined, w: World, present: number, dt: number): void {
    const u = playerId ? w.units[playerId] : undefined;
    if (!me || !u) return;
    let speed = 0;
    let canMove = true;
    try {
      speed = sim.queries.effectiveStats(u.id).moveSpeed;
      canMove = sim.queries.controlState(u.id).canMove;
    } catch {
      /* cosmetic only */
    }
    const prev = poses.get(u.id);
    const dir = paused ? { x: 0, y: 0 } : heldDir();
    // +multiplayer: the predicted own hero at the present (net/predict.ts); localMotion only blends
    // corrections. The path already holds the pending inputs (start, stop, dash, walls), so the
    // frame-to-frame motion follows its velocity, not the held keys.
    const own = net ? net.ownAt(0) : null;
    if (own && net) {
      const ahead = net.ownAt(OWN_VEL_MS) ?? own;
      const pm = motion.update({
        simPos: own,
        prevPos: null,
        simFacing: u.facing,
        tick: w.tick,
        heldDir: dir,
        simDir: null,
        speed: 0,
        autoMove: false,
        vel: { x: ((ahead.x - own.x) * 1000) / OWN_VEL_MS, y: ((ahead.y - own.y) * 1000) / OWN_VEL_MS },
        aheadMs: 0,
        dtMs: dt,
        alive: u.alive,
        ...(steersFacing(steering.scheme) ? { aimFacing: steering.facing } : {}), // +controls
      });
      me.pos = pm.pos;
      me.facing = pm.facing;
      me.speed = pm.speed;
      me.vel = pm.vel;
      me.steering = steering.isSteering(input.buttons);
      showLocalCast(me, u, present);
      return;
    }
    // +glide: in free flight the keys steer the glider, not the feet: extrapolate the flight velocity.
    const air = u.jump?.air && !u.jump.air.landed ? u.jump.air : null;
    const m = motion.update({
      simPos: u.pos,
      prevPos: prev ? { x: prev.x, y: prev.y } : null,
      simFacing: u.facing,
      tick: w.tick,
      heldDir: air ? { x: 0, y: 0 } : dir,
      ...(air ? { vel: { x: air.vel.x, y: air.vel.y } } : {}),
      simDir: u.move?.mode === 'dir' ? u.move.dir : null,
      speed: canMove && u.move?.mode !== 'forced' ? speed : 0,
      autoMove: !!u.move && u.move.mode !== 'dir',
      aheadMs: present - w.time + (net ? net.leadMs() : 0),
      ...(net ? { maxAheadMs: TICK_MS + net.leadMs() } : {}), // +multiplayer
      dtMs: paused ? 0 : dt,
      alive: u.alive,
      ...(steersFacing(steering.scheme) ? { aimFacing: steering.facing } : {}), // +controls
    });
    me.pos = m.pos;
    me.facing = m.facing;
    me.speed = m.speed;
    me.vel = m.vel;
    me.steering = steering.isSteering(input.buttons);
    if (u.jump && !u.jump.air) me.jumpH = jumpHeightAt(u.jump, present); // +jump: drawn at the present, like the run
    if (air) me.z = air.z + (air.vz * Math.max(-TICK_MS, Math.min(TICK_MS, present - w.time))) / 1000; // +glide: the height at the present too
    else if (u.perch) me.z = u.perch.z;
    me.glide = !!air?.glide;
    me.perched = !!u.perch && !air;
    showLocalCast(me, u, present);
  }

  /** The cast bar / pose of the own hero (the sim's cast, else the predicted press). */
  function showLocalCast(me: RenderUnit, u: World['units'][string], present: number): void {
    const pc = localFeedback.prediction?.cast;
    const c = u.cast ?? (pc && pc.abilityId ? pc : null);
    if (c) {
      me.casting = true;
      me.cast = { kind: c.kind, school: content.ability(c.abilityId)?.school ?? null, progress: Math.min(1, Math.max(0, (present - c.startedAt) / Math.max(1, c.endsAt - c.startedAt))), abilityId: c.abilityId };
    }
  }

  function probeFrame(f: { units: RenderUnit[]; cues?: AnimCue[]; selectedId: UnitId | null }, dt: number): void {
    if (!probe) return;
    const now = performance.now();
    const me = playerId ? f.units.find((u) => u.id === playerId) : undefined;
    for (const p of probe.pending('button')) {
      if (p.kind !== 'ability') continue;
      const slot = actionSlot(p.key);
      if (!slot) continue;
      const fb = slot.classList.contains('pressed') || slot.classList.contains('casting') || slot.classList.contains('queued') || (!gcdAtPress.get(p.key) && !!slot.querySelector('.action-gcd.on'));
      if (fb) probe.mark('button', now, (k, key) => k === 'ability' && key === p.key);
    }
    // Tab: the rendered frame carries the new selection (ring) and the target frame shows it.
    if (probe.pending('target').length && f.selectedId && f.selectedId !== selAtTab) probe.mark('target', now, (k) => k === 'tab');
    const castShown = !!me && (me.casting || !!f.cues?.some((c) => c.unitId === me.id && c.kind !== 'hit' && c.kind !== 'cancel') || !!opts.hudHost.querySelector('.player-cast.active'));
    if (castShown) probe.mark('cast', now, (k) => k === 'ability');
    if (me) {
      if (lastPlayerPos && Math.hypot(me.pos.x - lastPlayerPos.x, me.pos.y - lastPlayerPos.y) > 1e-4) probe.mark('move', now, (k) => k === 'move');
      lastPlayerPos = { x: me.pos.x, y: me.pos.y };
    }
    probe.frame(dt, now);
  }

  const onResize = (): void => renderer.resize(window.innerWidth, window.innerHeight);
  window.addEventListener('resize', onResize);
  onResize();
  raf = requestAnimationFrame(frame);


  /** +personas: the player's party chat line: into the next tick offline (logged for replays), straight to the room online. */
  function say(text: string): void {
    const line = text.trim().slice(0, CHAT_MAX_LEN);
    if (!playerId || !line || disposed) return;
    if (presenceHook.current?.say(line, playerId)) return; // +presence: in a shared field, typed chat goes to the zone
    const cmd: Command = { type: 'say', actorId: playerId, text: line };
    if (net) net.flush([cmd]);
    else saying.push(cmd);
  }

  return {
    sim,
    get renderer() {
      return renderer;
    },
    rig,
    setRenderer(factory) {
      if (disposed) return;
      const old = renderer;
      renderer = factory(opts.canvasHost);
      renderer.init(dungeon);
      applyQuality();
      onResize();
      old.dispose();
    },
    applySettings(s) {
      settings = s;
      autoQ.setCeiling(s.quality);
      renderer.applySettings?.(s);
      hud.setFxIntensity(s.fxIntensity);
    },
    debugFinish(o) {
      if (disposed || forced) return;
      const r = sim.result();
      const timed = o?.timed ?? true;
      forced = { completed: true, timed, chest: timed ? 1 : 0, forces: Math.max(r.forces, r.forcesRequired), elapsedMs: timed ? Math.min(r.elapsedMs, r.timerMs) : Math.max(r.elapsedMs, r.timerMs + 1) };
    },
    commandLog,
    say,
    playerId,
    command(cmd) {
      if (disposed) return;
      if (net) net.flush([cmd]);
      else {
        queued.push(cmd);
        requestTick();
      }
    },
    dispose() {
      disposed = true;
      cancelAnimationFrame(raf);
      driver.dispose();
      if (localFeedback.actorId === playerId) resetLocalFeedback();
      window.removeEventListener('resize', onResize);
      input.dispose();
      if (opts.field?.chaseCamera) worldFraming.pitch = rig.state.pitch; // +field
      unbindCamera(); // +camera
      cursorFx.dispose(); // +camera
      castux?.dispose(); // +castux
      unsubControls(); // +controls
      localControl.facing = null;
      probe?.dispose();
      perf.dispose();
      hud.dispose();
      hookOk(() => opts.hooks?.dispose?.(), undefined); // +tutorial
      renderer.dispose();
    },
  };
}

/** +field: the open-world camera (chase view). Pitch persists across map loads within the page. */
export const WORLD_DISTANCE = 13;
export const WORLD_PITCH = 0.22;
const worldFraming: { pitch: number | null } = { pitch: null };

function applyWorldFraming(rig: CameraRig): void {
  if (Math.abs(cameraSettings.get().zoom - DEFAULT_DISTANCE) < 0.01) rig.setDistance(WORLD_DISTANCE);
  rig.state.pitch = worldFraming.pitch ?? WORLD_PITCH;
}

/**
 * +personas: who plays the bot slots. Explicit roster, or three default companions from the run seed
 * (one per bot slot class; spectating, the main hero's own slot, the looter, plays without one).
 */
function sessionPersonas(opts: SessionOptions): readonly Persona[] {
  if (opts.personas !== undefined) return opts.personas ?? [];
  const slot = opts.config.slot;
  const classes = opts.party.filter((_, i) => i !== slot && !(slot < 0 && opts.looterId === `p${i + 1}`));
  try {
    return defaultCompanions(classes, opts.config.seed, opts.partyNames ? [...opts.partyNames] : []);
  } catch (err) {
    console.warn('[web] personas unavailable, bots play without them', err);
    return [];
  }
}
